import React, {Fragment, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {useEvent} from "../hooks/useEvent";
import {cancelBooking, findEventProducts} from "../api/apiClient";
import {toast} from "react-toastify";
import {useStateContext} from "../contexts/ContextProvider";
import {getLowStockLabel, isProductSoldOut, PRODUCT_SOLD_OUT_LABEL} from "../utility/productAvailability";
import HtmlContent from "../components/HtmlContent";

const EventPage = () => {
    const {user} = useStateContext();
    const {eventUuid} = useParams();
    const navigate = useNavigate();
    const [reloadEvent, setReloadEvent] = useState(0);
    const {event} = useEvent(eventUuid, reloadEvent);
    const [isOverlayOpen, setOverlayOpen] = useState(false);
    const [isConfirmVisible, setIsConfirmVisible] = useState(false);
    const [bookingToCancel, setBookingToCancel] = useState(null);
    const [products, setProducts] = useState([]);
    const [cancelling, setCancelling] = useState(false);

    if (!event || Object.keys(event).length === 0) {
        return <div>Loading...</div>;
    }

    const fetchProducts = () => {
        findEventProducts(eventUuid)
            .then((response) => {
                setProducts(response);
                setOverlayOpen(true);
            })
            .catch((error) => {
                console.error("Failed to fetch products:", error);
            });
    };

    const closeOverlay = () => {
        setOverlayOpen(false);
        setProducts([]);
    };

    const handleCancelBooking = async () => {
        if (!bookingToCancel) {
            return;
        }

        setCancelling(true);

        cancelBooking(bookingToCancel.bookingUuid)
            .then(() => {
                setIsConfirmVisible(false);
                setReloadEvent((prev) => prev + 1);
                toast.success("Buchung erfolgreich storniert!");
            })
            .catch((error) => {
                toast.error("Fehler beim Stornieren der Buchung.");
                console.error(error);
            })
            .finally(() => {
                setCancelling(false);
            });
    };

    const showConfirmOverlay = () => {
        if (!event || !event.eventTimes || event.eventTimes.length === 0) {
            console.error("No event times available for confirmation overlay.");
            return;
        }

        const bookingUuid = event.bookingUuid;
        const eventName = event.name;
        const startedAt = event.eventTimes[0].startedAt; // Get the startedAt of the first event time
        const cancellationCreditRefundPeriodInHours = event.cancellationCreditRefundPeriodInHours;
        const canCancelWithCreditBack = event.canCancelWithCreditBack;

        setBookingToCancel(
            {
                bookingUuid,
                eventName,
                startedAt,
                cancellationCreditRefundPeriodInHours,
                canCancelWithCreditBack,
            }
        );
        setIsConfirmVisible(true);
    };

    const availableSeats = event.seats - event.bookedSeats;

    return (
        <Fragment>
            <div className="container mx-auto max-w-[760px] p-6 bg-white rounded-lg shadow-md relative">
                {/* Zurück Link */}
                <div className="mb-4">
                    <button
                        className="text-pegasusPurple hover:text-pink-700 font-medium"
                        onClick={() => navigate(-1)}
                    >
                        &lt; Zurück
                    </button>
                </div>

                {/* Event Header */}
                <div className="border-b pb-4">
                    <h1 className="text-xl font-bold">{event.name}</h1>
                    <div className="flex items-center mt-2">
                        <div className="bg-pegasusPurple text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-semibold">
                            {event.trainerNames.split(", ").map((name) => name[0]).join("")}
                        </div>
                        <p className="ml-2 font-medium">{event.trainerNames}</p>
                    </div>
                </div>

                {/* Event Times */}
                <div className="my-6">
                    <ul className="list-disc ml-6 text-gray-700">
                        {event.eventTimes.map((time, index) => (
                            <li key={index}>
                                {new Date(time.startedAt).toLocaleDateString("de-DE", {
                                    weekday : "short",
                                    day     : "2-digit",
                                    month   : "long",
                                    year    : "numeric",
                                })}{" "}
                                {new Date(time.startedAt).toLocaleTimeString("de-DE", {
                                    hour   : "2-digit",
                                    minute : "2-digit",
                                })}{" "}
                                -{" "}
                                {new Date(time.endedAt).toLocaleTimeString("de-DE", {
                                    hour   : "2-digit",
                                    minute : "2-digit",
                                })}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Smart Lock Info Box */}
                {event.requiresSmartLock && (
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 rounded-lg p-4 mb-6">
                        <h2 className="font-bold mb-2">Wichtiger Hinweis zur Buchung:</h2>
                        <p className="mb-3">
                            Bei diesem Termin ist keine Trainerin vor Ort. Du trainierst
                            selbstständig, eigenverantwortlich und ohne Anleitung. Der Zugang zum
                            Studio erfolgt über unsere Schlüsselbox mit deinem persönlichen
                            PIN-Code.
                        </p>
                        <p className="font-semibold mb-1">Einmalige Freischaltung erforderlich:</p>
                        <p>
                            Um diesen Termin buchen und nutzen zu können, muss dein Account
                            einmalig von uns freigeschaltet werden. Nach erfolgreicher Buchung
                            erhältst du deinen persönlichen PIN-Code und eine ausführliche
                            Zugangsanleitung per E-Mail.
                        </p>
                        <p>
                            Du bist noch nicht freigeschaltet? Dann sprich uns einfach kurz im
                            Studio an oder schreibe uns eine Nachricht!
                        </p>
                    </div>
                )}

                {/* Event Image */}
                {event.image && (
                    <div className="mb-6">
                        <img
                            src={event.image}
                            alt={event.name}
                            className="w-full h-auto rounded-md"
                        />
                    </div>
                )}

                {/* Event Details */}
                <div className="space-y-4">
                    {/* Location */}
                    <div className="flex justify-between border-b pb-2">
                        <h2 className="text-gray-600 font-semibold">Standort:</h2>
                        <p className="text-gray-800">{event.locationName}</p>
                    </div>

                    {/* Availability */}
                    <div className="flex justify-between border-b pb-2">
                        <h2 className="text-gray-600 font-semibold">Verfügbarkeit:</h2>
                        <p className={`${availableSeats <= 3 ? "font-bold" : ""}`}>
                            {availableSeats > 0 ? (
                                availableSeats === 1 ? (
                                    "Nur noch 1 Platz frei"
                                ) : availableSeats <= 3 ? (
                                    `Nur noch ${availableSeats} Plätze frei`
                                ) : (
                                    `${availableSeats} Plätze frei`
                                )
                            ) : (
                                "Ausgebucht"
                            )}
                        </p>
                    </div>

                    {/* Booking and Price Buttons */}
                    <div className="space-y-2 border-b pb-2">
                        {event.bookedAt ? (
                            <button
                                className="w-full py-2 rounded-lg font-semibold bg-pegasusPurple text-white hover:bg-pegasusPurpleHover"
                                onClick={showConfirmOverlay}
                            >
                                Buchung stornieren
                            </button>
                        ) : (
                            <button
                                className={`w-full py-2 rounded-lg font-semibold ${
                                    availableSeats > 0
                                        ? "bg-pegasusPurple text-white hover:bg-pegasusPurpleHover"
                                        : "bg-gray-300 text-gray-600 cursor-not-allowed"
                                }`}
                                disabled={availableSeats <= 0}
                                onClick={() => navigate(`/events/${eventUuid}/booking-products`)}
                            >
                                Jetzt buchen
                            </button>
                        )}

                        <button
                            className="w-full border border-gray-300 text-gray-700 font-semibold py-2 rounded-lg hover:bg-gray-100"
                            onClick={fetchProducts}
                        >
                            Preise für dieses Event anzeigen
                        </button>
                    </div>

                    {/* Booking Until */}
                    <div className="flex justify-between border-b pb-2">
                        <h2 className="text-gray-600 font-semibold">Buchbar bis:</h2>
                        <p className="text-gray-800">
                            {new Date(event.bookableUntil).toLocaleDateString("de-DE", {
                                weekday : "short",
                                day     : "2-digit",
                                month   : "long",
                                year    : "numeric",
                            })}
                        </p>
                    </div>

                    {/* Kursbeschreibung */}
                    <div>
                        <h2 className="text-gray-600 font-semibold">Kursbeschreibung:</h2>
                        <HtmlContent className="mt-2" html={event.description}/>
                    </div>
                </div>
            </div>

            {/* Confirmation Overlay */}
            {isConfirmVisible && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-96">
                        <h2 className="text-lg font-bold text-gray-800 mb-2">
                            Hey, {user?.firstName || "Gast"}!
                        </h2>
                        <p className="text-gray-700 mb-4">
                            Bist du sicher, dass du deine Buchung stornieren möchtest? <br/>
                            {
                                bookingToCancel?.canCancelWithCreditBack
                                    ? "Deine Einheit wird zurückerstattet."
                                    : "Bei Stornierungen innerhalb von " + bookingToCancel?.cancellationCreditRefundPeriodInHours + " Stunden vor Veranstaltungsbegin erfolgt keine Rückerstattung."
                            }
                        </p>
                        <div className="text-center mb-4">
                            <p className="font-bold text-gray-900">{bookingToCancel?.name}</p>
                            <p className="text-sm text-gray-500">
                                {new Date(bookingToCancel?.startedAt).toLocaleDateString(
                                    "de-DE",
                                    {
                                        weekday : "long",
                                        day     : "2-digit",
                                        month   : "2-digit",
                                        year    : "numeric",
                                    }
                                )}{" "}
                                |{" "}
                                {new Date(bookingToCancel?.startedAt).toLocaleTimeString(
                                    "de-DE",
                                    {
                                        hour   : "2-digit",
                                        minute : "2-digit",
                                    }
                                )}
                            </p>
                        </div>
                        <button
                            onClick={handleCancelBooking}
                            disabled={cancelling}
                            className={`w-full py-2 rounded-lg font-bold flex items-center justify-center ${
                                cancelling
                                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                                    : "bg-pegasusPurple text-white hover:bg-pegasusPurpleHover"
                            }`}
                        >
                            {cancelling && (
                                <span className="inline-block w-4 h-4 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                            )}
                            {cancelling ? "Wird storniert..." : "BESTÄTIGE STORNIERUNG"}
                        </button>
                        <button
                            onClick={() => setIsConfirmVisible(false)}
                            disabled={cancelling}
                            className="text-pegasusPurple mt-2 underline w-full disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Abbrechen
                        </button>
                    </div>
                </div>
            )}

            {/* Overlay */}
            {isOverlayOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white max-w-[760px] w-full p-6 rounded-lg shadow-lg">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-bold">Preise für dieses Event</h2>
                            <button
                                className="text-gray-500 hover:text-gray-700"
                                onClick={closeOverlay}
                            >
                                Schließen ✕
                            </button>
                        </div>
                        <div className="space-y-4">
                            {products.map((product) => {
                                const soldOut = isProductSoldOut(product);
                                const lowStockLabel = getLowStockLabel(product);

                                return (
                                    <div
                                        key={product.uuid}
                                        className={`flex justify-between items-center border-b pb-4 ${soldOut ? "opacity-60" : ""}`}
                                    >
                                        <div>
                                            <h3 className="font-semibold text-gray-800">{product.name}</h3>
                                            <HtmlContent className="text-gray-600" html={product.description}/>
                                            {lowStockLabel && (
                                                <p className="text-sm font-medium text-pegasusPurple mt-1">{lowStockLabel}</p>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            <p className={`font-semibold ${soldOut ? "text-gray-500 line-through" : "text-gray-800"}`}>
                                                {product.bruttoPrice.toFixed(2)} {product.currencySign}
                                            </p>
                                            {soldOut ? (
                                                <span className="inline-block mt-1 bg-gray-200 text-gray-700 text-sm font-semibold px-2 py-1 rounded">
                                                    {PRODUCT_SOLD_OUT_LABEL}
                                                </span>
                                            ) : (
                                                <p className="text-gray-500 text-sm">Einmalig</p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export default EventPage;
